(() => {
  'use strict';

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));

  const formatMoney = (cents, format = window.theme?.moneyFormat || '${{amount}}') => {
    const value = Number(cents || 0);
    const withDelimiter = (number, precision = 2, thousands = ',', decimal = '.') => {
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return `$${withDelimiter(value)}`;
    let formatted;
    switch (match[1]) {
      case 'amount_no_decimals': formatted = withDelimiter(value, 0); break;
      case 'amount_with_comma_separator': formatted = withDelimiter(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': formatted = withDelimiter(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': formatted = withDelimiter(value, 2, "'", '.'); break;
      default: formatted = withDelimiter(value);
    }
    return format.replace(match[0], formatted);
  };

  const toast = (message) => {
    const element = $('[data-toast]');
    if (!element) return;
    element.textContent = message;
    element.hidden = false;
    clearTimeout(element._timer);
    element._timer = setTimeout(() => { element.hidden = true; }, 3200);
  };

  /* Accessible drawers */
  let activeDrawer = null;
  let lastTrigger = null;
  const overlay = $('[data-drawer-overlay]');

  const openDrawer = (id, trigger) => {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(false);
    activeDrawer = drawer;
    lastTrigger = trigger || document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.hidden = false;
    document.body.classList.add('drawer-open');
    requestAnimationFrame(() => $('[data-drawer-close],button,a,input,select', drawer)?.focus());
  };

  const closeDrawer = (restoreFocus = true) => {
    if (!activeDrawer) return;
    activeDrawer.classList.remove('is-open');
    activeDrawer.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.hidden = true;
    document.body.classList.remove('drawer-open');
    if (restoreFocus && lastTrigger instanceof HTMLElement) lastTrigger.focus();
    activeDrawer = null;
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener.dataset.drawerOpen, opener);
      return;
    }
    if (event.target.closest('[data-drawer-close]')) closeDrawer();
  });
  overlay?.addEventListener('click', () => closeDrawer());
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeDrawer(); });

  /* Announcement rotator */
  $$('.announcement').forEach((bar) => {
    const items = $$('.announcement__item', bar);
    if (items.length < 2) return;
    let current = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
    const show = (index) => {
      items[current].classList.remove('is-active');
      current = (index + items.length) % items.length;
      items[current].classList.add('is-active');
    };
    $('[data-announcement-prev]', bar)?.addEventListener('click', () => show(current - 1));
    $('[data-announcement-next]', bar)?.addEventListener('click', () => show(current + 1));
    const delay = Number(bar.dataset.autoplay || 0) * 1000;
    if (delay > 0 && !matchMedia('(prefers-reduced-motion: reduce)').matches) setInterval(() => show(current + 1), delay);
  });

  /* Desktop category menu */
  $$('[data-category-trigger]').forEach((trigger) => {
    const menu = document.getElementById(trigger.getAttribute('aria-controls'));
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      const willOpen = !menu?.classList.contains('is-open');
      menu?.classList.toggle('is-open', willOpen);
      trigger.setAttribute('aria-expanded', String(willOpen));
    });
    document.addEventListener('click', (event) => {
      if (!event.target.closest('.category-nav')) {
        menu?.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
  });

  /* Predictive search */
  $$('.header-search').forEach((container) => {
    const input = $('input[type="search"]', container);
    const panel = $('[data-predictive-search]', container);
    if (!input || !panel) return;
    let timer;
    let controller;
    const close = () => { panel.hidden = true; panel.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); };
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) { close(); return; }
      timer = setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        panel.innerHTML = '<div class="predictive-search__group">Searching…</div>';
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        try {
          const url = `${window.theme.routes.predictiveSearch}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search`;
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('Search failed');
          panel.innerHTML = await response.text();
        } catch (error) {
          if (error.name !== 'AbortError') close();
        }
      }, 220);
    });
    document.addEventListener('click', (event) => { if (!container.contains(event.target)) close(); });
    input.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
  });

  /* Quantity controls */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-change]');
    if (!button) return;
    const wrapper = button.closest('.quantity');
    const input = $('input[type="number"]', wrapper);
    if (!input) return;
    const step = button.dataset.quantityChange === 'increase' ? 1 : -1;
    const min = Number(input.min || 1);
    const max = Number(input.max || Infinity);
    input.value = String(Math.min(max, Math.max(min, Number(input.value || min) + step)));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Product gallery */
  $$('[data-product-gallery]').forEach((gallery) => {
    const thumbs = $$('[data-media-id]', gallery);
    const slides = $$('[data-media-slide]', gallery);
    const activate = (id) => {
      thumbs.forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.mediaId === id));
      slides.forEach((slide) => slide.classList.toggle('is-active', slide.dataset.mediaSlide === id));
    };
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => activate(thumb.dataset.mediaId)));
    $$('[data-media-zoom]', gallery).forEach((button) => button.addEventListener('click', () => {
      const dialog = document.getElementById(button.dataset.mediaZoom);
      if (dialog?.showModal) dialog.showModal();
    }));
  });
  $$('[data-dialog-close]').forEach((button) => button.addEventListener('click', () => button.closest('dialog')?.close()));
  $$('dialog').forEach((dialog) => dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); }));

  /* Variant selection */
  $$('[data-product-section]').forEach((section) => {
    const json = $('[data-product-json]', section);
    const form = $('[data-type="product-form"]', section);
    if (!json || !form) return;
    let product;
    try { product = JSON.parse(json.textContent); } catch (_) { return; }
    const idInput = $('input[name="id"]', form);
    const addButton = $('[name="add"]', form);
    const price = $('[data-product-price]', section);
    const compare = $('[data-product-compare-price]', section);
    const discount = $('[data-product-discount]', section);
    const stock = $('[data-product-stock]', section);
    const stickyPrice = $('[data-sticky-price]', section);
    const stickyButton = $('[data-sticky-add]', section);
    const buyNowButton = $('[data-main-buy-now]', section);
    const stickyBuyNowButton = $('[data-sticky-buy-now]', section);

    const selectedOptions = () => product.options.map((_, index) => {
      const position = index + 1;
      const checked = $(`[data-option-position="${position}"]:checked`, form);
      const fixed = $(`input[type="hidden"][data-option-position="${position}"]`, form);
      return (checked || fixed)?.value || '';
    });

    const updateOptionAvailability = (selected) => {
      $$('[data-option-input][type="radio"]', form).forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        const available = product.variants.some((item) => {
          if (!item.available || item.options[position] !== input.value) return false;
          return item.options.slice(0, position).every((value, index) => value === selected[index]);
        });
        input.disabled = !available;
        input.closest('label')?.classList.toggle('is-unavailable', !available);
      });
    };

    const update = () => {
      const selected = selectedOptions();
      const variant = product.variants.find((item) => item.options.every((option, index) => option === selected[index]));
      if (!variant) {
        idInput.value = '';
        addButton.disabled = true;
        addButton.textContent = window.theme.strings.unavailable;
        if (stickyButton) {
          stickyButton.disabled = true;
          stickyButton.textContent = window.theme.strings.unavailable;
        }
        if (buyNowButton) buyNowButton.disabled = true;
        if (stickyBuyNowButton) stickyBuyNowButton.disabled = true;
        updateOptionAvailability(selected);
        return;
      }
      idInput.value = variant.id;
      if (price) price.textContent = formatMoney(variant.price);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (compare) {
        compare.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !(variant.compare_at_price > variant.price);
      }
      if (discount) {
        const percentage = variant.compare_at_price > variant.price ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100) : 0;
        discount.textContent = percentage ? `Save ${percentage}%` : '';
        discount.hidden = !percentage;
      }
      addButton.disabled = !variant.available;
      addButton.textContent = variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;
      if (stickyButton) {
        stickyButton.disabled = !variant.available;
        stickyButton.textContent = variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;
      }
      if (buyNowButton) buyNowButton.disabled = !variant.available;
      if (stickyBuyNowButton) stickyBuyNowButton.disabled = !variant.available;
      if (stock) {
        stock.textContent = variant.available ? stock.dataset.inStock : stock.dataset.outOfStock;
        stock.classList.toggle('stock-status--out', !variant.available);
      }
      if (variant.featured_media?.id) {
        section.querySelector(`[data-media-id="${variant.featured_media.id}"]`)?.click();
      }
      $$('[data-option-input][type="radio"]:checked', form).forEach((input) => {
        const fieldset = input.closest('.product-option');
        const label = $('[data-selected-option]', fieldset);
        if (label) label.textContent = input.value;
      });
      updateOptionAvailability(selected);
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    };
    $$('[data-option-input]', form).forEach((input) => input.addEventListener('change', update));
    update();

    const stickyBar = $('[data-sticky-atc]', section);
    if (stickyBar && stickyButton && addButton) {
      stickyButton.addEventListener('click', () => addButton.click());
      stickyBuyNowButton?.addEventListener('click', () => buyNowButton?.click());
      const syncSticky = () => {
        if (window.innerWidth > 749) {
          stickyBar.classList.remove('is-visible');
          stickyBar.setAttribute('aria-hidden', 'true');
          return;
        }
        const rect = addButton.getBoundingClientRect();
        const visible = rect.bottom < 0;
        stickyBar.classList.toggle('is-visible', visible);
        stickyBar.setAttribute('aria-hidden', String(!visible));
      };
      document.addEventListener('scroll', syncSticky, { passive: true });
      window.addEventListener('resize', syncSticky, { passive: true });
      syncSticky();
    }
  });

  /* AJAX cart */
  const cartDrawer = document.getElementById('CartDrawer');
  const renderCart = (cart) => {
    $$('[data-cart-count]').forEach((element) => { element.textContent = cart.item_count; element.hidden = cart.item_count === 0; });
    $$('[data-cart-subtotal]').forEach((element) => { element.textContent = formatMoney(cart.total_price); });
    const itemsTarget = $('[data-cart-items]', cartDrawer);
    const emptyTarget = $('[data-cart-empty]', cartDrawer);
    const footer = $('[data-cart-footer]', cartDrawer);
    if (!itemsTarget) return;
    if (!cart.items.length) {
      itemsTarget.innerHTML = '';
      if (emptyTarget) emptyTarget.hidden = false;
      if (footer) footer.hidden = true;
      return;
    }
    if (emptyTarget) emptyTarget.hidden = true;
    if (footer) footer.hidden = false;
    itemsTarget.innerHTML = cart.items.map((item) => {
      const variants = item.options_with_values?.filter((option) => option.value !== 'Default Title').map((option) => `${escapeHTML(option.name)}: ${escapeHTML(option.value)}`).join(' · ') || '';
      const image = item.image ? `<img class="cart-item__image" src="${escapeHTML(item.image)}&width=180" alt="${escapeHTML(item.product_title)}" width="90" height="90">` : '<div class="cart-item__image"></div>';
      return `<div class="cart-item" data-cart-line="${escapeHTML(item.key)}">
        <a href="${escapeHTML(item.url)}">${image}</a>
        <div><a class="cart-item__title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>${variants ? `<div class="cart-item__variant">${variants}</div>` : ''}
          <div class="quantity"><button type="button" aria-label="Decrease quantity" data-cart-change="${escapeHTML(item.key)}" data-cart-quantity="${Math.max(0, item.quantity - 1)}">−</button><input value="${item.quantity}" readonly aria-label="Quantity"><button type="button" aria-label="Increase quantity" data-cart-change="${escapeHTML(item.key)}" data-cart-quantity="${item.quantity + 1}">+</button></div>
          <button class="cart-remove" type="button" data-cart-change="${escapeHTML(item.key)}" data-cart-quantity="0">Remove</button>
        </div><div class="cart-item__price">${formatMoney(item.final_line_price)}</div>
      </div>`;
    }).join('');
    updateShippingProgress(cart.total_price);
  };

  const updateShippingProgress = (total) => {
    $$('[data-shipping-progress]').forEach((wrapper) => {
      const threshold = Number(wrapper.dataset.threshold || 0);
      if (!threshold) return;
      const percent = Math.min(100, (total / threshold) * 100);
      wrapper.style.setProperty('--progress', `${percent}%`);
      const text = $('[data-shipping-progress-text]', wrapper);
      if (text) text.textContent = total >= threshold ? wrapper.dataset.complete : wrapper.dataset.remaining.replace('[amount]', formatMoney(threshold - total));
    });
  };

  const getCart = async () => {
    const response = await fetch(`${window.theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-type="product-form"], [data-type="quick-add-form"]');
    if (!form || !window.fetch) return;
    if (form.dataset.type === 'product-form' && event.submitter && event.submitter.name !== 'add') return;
    event.preventDefault();
    const button = $('[type="submit"]', form);
    const original = button?.innerHTML;
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    try {
      const response = await fetch(`${window.theme.routes.cartAdd}.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || window.theme.strings.cartError);
      await getCart();
      if (window.themeSettings?.enableCartDrawer !== false && cartDrawer) openDrawer('CartDrawer', button);
      else location.href = window.theme.routes.cart;
    } catch (error) {
      toast(error.message || window.theme.strings.cartError);
    } finally {
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); button.innerHTML = original; }
    }
  });

  document.addEventListener('click', async (event) => {
    const control = event.target.closest('[data-cart-change]');
    if (!control) return;
    event.preventDefault();
    control.disabled = true;
    try {
      const response = await fetch(`${window.theme.routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: control.dataset.cartChange, quantity: Number(control.dataset.cartQuantity) }) });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || window.theme.strings.cartError);
      renderCart(cart);
    } catch (error) {
      toast(error.message || window.theme.strings.cartError);
    } finally { control.disabled = false; }
  });

  /* Product recommendations */
  $$('[data-recommendations-url]').forEach(async (container) => {
    try {
      const response = await fetch(container.dataset.recommendationsUrl);
      if (!response.ok) return;
      const html = document.createElement('div');
      html.innerHTML = await response.text();
      const replacement = html.querySelector('[data-recommendations-url]');
      if (replacement?.innerHTML.trim()) container.innerHTML = replacement.innerHTML;
    } catch (_) { /* recommendations are optional */ }
  });

  /* Recently viewed */
  const recentKey = 'gadget-mart-recent-products';
  $$('[data-current-product]').forEach((element) => {
    try {
      const product = JSON.parse(element.textContent);
      const products = JSON.parse(localStorage.getItem(recentKey) || '[]').filter((item) => item.handle !== product.handle);
      products.unshift(product);
      localStorage.setItem(recentKey, JSON.stringify(products.slice(0, 8)));
    } catch (_) { /* storage can be unavailable */ }
  });
  $$('[data-recently-viewed]').forEach((container) => {
    try {
      const current = container.dataset.currentHandle;
      const products = JSON.parse(localStorage.getItem(recentKey) || '[]').filter((item) => item.handle !== current).slice(0, Number(container.dataset.limit || 6));
      if (!products.length) return;
      const grid = $('[data-recent-grid]', container);
      grid.innerHTML = products.map((item) => `<article class="product-card"><a class="product-card__media ratio-square" href="${escapeHTML(item.url)}"><img src="${escapeHTML(item.image)}" alt="${escapeHTML(item.title)}" loading="lazy" width="360" height="360"></a><div class="product-card__body"><a class="product-card__title" href="${escapeHTML(item.url)}">${escapeHTML(item.title)}</a><div class="price"><span class="price__current">${escapeHTML(item.price)}</span></div></div></article>`).join('');
      container.hidden = false;
    } catch (_) { /* no recent history */ }
  });

  window.themeSettings = window.themeSettings || {};
})();

/* ==========================================================================
   Gadget Mart Pro v1.1 — carousels, tabs, countdown, wishlist, gallery swipe
   ========================================================================== */
(() => {
  'use strict';
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Universal carousel ---------- */
  const initCarousel = (root) => {
    const track = $('[data-carousel-track]', root);
    if (!track || root.dataset.carouselReady === 'true') return;
    root.dataset.carouselReady = 'true';

    const prev = $('[data-carousel-prev]', root);
    const next = $('[data-carousel-next]', root);
    const dotsWrap = $('[data-carousel-dots]', root);
    const autoplay = root.dataset.autoplay === 'true';
    const interval = Math.max(1, Number(root.dataset.interval || 4)) * 1000;
    const forward = (root.dataset.direction || 'rtl') !== 'ltr';

    const cardStep = () => {
      const first = track.firstElementChild;
      if (!first) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap || '12') || 12;
      return first.getBoundingClientRect().width + gap;
    };
    const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const syncControls = () => {
      const x = Math.round(track.scrollLeft);
      const max = Math.round(maxScroll());
      if (prev) prev.disabled = x <= 2;
      if (next) next.disabled = x >= max - 2;
      if (dotsWrap) {
        const buttons = $$('button', dotsWrap);
        if (buttons.length) {
          const page = track.clientWidth ? Math.round(x / track.clientWidth) : 0;
          buttons.forEach((b, i) => b.classList.toggle('is-active', i === page));
        }
      }
    };

    const buildDots = () => {
      if (!dotsWrap) return;
      const pages = track.clientWidth ? Math.ceil(track.scrollWidth / track.clientWidth) : 1;
      dotsWrap.innerHTML = '';
      if (pages < 2) return;
      for (let i = 0; i < pages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Go to page ${i + 1}`);
        dot.addEventListener('click', () => track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' }));
        dotsWrap.appendChild(dot);
      }
      syncControls();
    };

    const page = (dir) => track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
    prev?.addEventListener('click', () => page(-1));
    next?.addEventListener('click', () => page(1));
    track.addEventListener('scroll', () => { window.requestAnimationFrame(syncControls); }, { passive: true });
    window.addEventListener('resize', () => { buildDots(); syncControls(); });

    /* Pointer drag on desktop */
    let dragging = false;
    let startX = 0;
    let startScroll = 0;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      dragging = true;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      track.style.scrollBehavior = 'auto';
    });
    track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      track.scrollLeft = startScroll - (event.clientX - startX);
    });
    const endDrag = () => { dragging = false; track.style.scrollBehavior = ''; };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointerleave', endDrag);
    track.addEventListener('pointercancel', endDrag);

    /* Autoplay */
    if (autoplay && !reduceMotion) {
      let timer = null;
      let paused = false;
      const tick = () => {
        if (paused || document.hidden || maxScroll() <= 0) return;
        const step = cardStep();
        const x = track.scrollLeft;
        if (forward) {
          if (x >= maxScroll() - 2) track.scrollTo({ left: 0, behavior: 'smooth' });
          else track.scrollBy({ left: step, behavior: 'smooth' });
        } else if (x <= 2) {
          track.scrollTo({ left: maxScroll(), behavior: 'smooth' });
        } else {
          track.scrollBy({ left: -step, behavior: 'smooth' });
        }
      };
      const start = () => { clearInterval(timer); timer = setInterval(tick, interval); };
      const pause = () => { paused = true; };
      const resume = () => { paused = false; };
      root.addEventListener('mouseenter', pause);
      root.addEventListener('mouseleave', resume);
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', resume);
      track.addEventListener('touchstart', pause, { passive: true });
      track.addEventListener('touchend', () => setTimeout(resume, 2500), { passive: true });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) start(); });
      if (!forward) requestAnimationFrame(() => { track.scrollLeft = maxScroll(); });
      start();
    }

    buildDots();
    requestAnimationFrame(syncControls);
  };

  const initAllCarousels = (scope = document) => $$('[data-carousel]', scope).forEach(initCarousel);
  initAllCarousels();

  /* ---------- Tabs ---------- */
  $$('[data-tabs]').forEach((group) => {
    const buttons = $$('[data-tab-button]', group);
    const panels = $$('[data-tab-panel]', group);
    if (!buttons.length) return;
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.dataset.tabButton;
        buttons.forEach((other) => {
          const active = other === button;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-selected', String(active));
        });
        panels.forEach((panel) => {
          const active = panel.dataset.tabPanel === id;
          panel.classList.toggle('is-active', active);
          if (active) initAllCarousels(panel);
        });
      });
    });
  });

  /* ---------- Countdown ---------- */
  $$('[data-countdown]').forEach((element) => {
    const hours = $('[data-countdown-hours]', element);
    const minutes = $('[data-countdown-minutes]', element);
    const seconds = $('[data-countdown-seconds]', element);
    const repeat = element.dataset.repeat === 'true';
    const raw = (element.dataset.end || '').trim().replace(' ', 'T');
    let target = new Date(raw);
    if (Number.isNaN(target.getTime())) { element.hidden = true; return; }

    const pad = (value) => String(Math.max(0, Math.floor(value))).padStart(2, '0');
    const render = () => {
      let diff = target.getTime() - Date.now();
      if (diff <= 0) {
        if (repeat) {
          const day = 86400000;
          target = new Date(target.getTime() + Math.ceil((Date.now() - target.getTime()) / day) * day);
          diff = target.getTime() - Date.now();
        } else {
          if (hours) hours.textContent = '00';
          if (minutes) minutes.textContent = '00';
          if (seconds) seconds.textContent = '00';
          return false;
        }
      }
      const total = Math.floor(diff / 1000);
      if (hours) hours.textContent = pad(total / 3600);
      if (minutes) minutes.textContent = pad((total % 3600) / 60);
      if (seconds) seconds.textContent = pad(total % 60);
      return true;
    };
    if (render()) setInterval(render, 1000);
  });

  /* ---------- Wishlist hearts ---------- */
  const wishKey = 'gadget-mart-wishlist';
  const readWish = () => { try { return JSON.parse(localStorage.getItem(wishKey) || '[]'); } catch (_) { return []; } };
  const wished = readWish();
  $$('[data-wishlist]').forEach((button) => {
    if (wished.includes(button.dataset.wishlist)) button.setAttribute('aria-pressed', 'true');
    button.addEventListener('click', () => {
      const handle = button.dataset.wishlist;
      const list = readWish();
      const index = list.indexOf(handle);
      if (index > -1) list.splice(index, 1); else list.push(handle);
      button.setAttribute('aria-pressed', String(index === -1));
      try { localStorage.setItem(wishKey, JSON.stringify(list)); } catch (_) { /* storage may be blocked */ }
    });
  });

  /* ---------- Product gallery: arrows, counter, swipe ---------- */
  $$('[data-product-gallery]').forEach((gallery) => {
    const slides = $$('[data-media-slide]', gallery);
    const thumbs = $$('[data-media-id]', gallery);
    if (slides.length < 2) {
      $$('.product-gallery__nav, .product-gallery__counter', gallery).forEach((node) => node.remove());
      return;
    }
    const counter = $('[data-gallery-counter]', gallery);
    const indexOfActive = () => Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    const go = (index) => {
      const target = (index + slides.length) % slides.length;
      const id = slides[target].dataset.mediaSlide;
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === target));
      thumbs.forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.mediaId === id));
      const activeThumb = thumbs.find((thumb) => thumb.dataset.mediaId === id);
      activeThumb?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      if (counter) counter.textContent = `${target + 1} / ${slides.length}`;
    };

    if (counter) counter.textContent = `${indexOfActive() + 1} / ${slides.length}`;
    $('[data-gallery-prev]', gallery)?.addEventListener('click', () => go(indexOfActive() - 1));
    $('[data-gallery-next]', gallery)?.addEventListener('click', () => go(indexOfActive() + 1));
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => {
      if (counter) {
        const i = slides.findIndex((slide) => slide.dataset.mediaSlide === thumb.dataset.mediaId);
        if (i > -1) counter.textContent = `${i + 1} / ${slides.length}`;
      }
    }));

    const main = $('.product-gallery__main', gallery);
    let touchX = null;
    let touchY = null;
    main?.addEventListener('touchstart', (event) => {
      touchX = event.changedTouches[0].clientX;
      touchY = event.changedTouches[0].clientY;
    }, { passive: true });
    main?.addEventListener('touchend', (event) => {
      if (touchX === null) return;
      const dx = event.changedTouches[0].clientX - touchX;
      const dy = event.changedTouches[0].clientY - touchY;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) go(indexOfActive() + (dx < 0 ? 1 : -1));
      touchX = null;
    }, { passive: true });
  });

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (event) => {
    initAllCarousels(event.target);
  });
})();
